import type { CommentDto, FollowDto } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { ApiError, newOpId } from '../../platform/api.js';
import { drafts } from '../../platform/drafts.js';
import { keys, queryClient } from '../../platform/queries.js';
import { removeComment, upsertComment } from '../../platform/realtime.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';

/**
 * Schreibwege für Zusammenarbeit (Paket P6) über den Ausgangskorb (ADR 0007): Kommentare erscheinen sofort und
 * überleben Neuladen und Funklöcher; abgelehnte Texte kommen zurück ins Eingabefeld.
 */

const messageOf = (err: unknown) => (err instanceof Error ? err.message : t('task.failed'));
export const commentDraftKey = (taskId: string) => `comment.${taskId}`;

outbox.register('comment.create', {
  done: (e, r) => upsertComment(e.projectId, r as CommentDto),
  fail: (e, err) => {
    const body = (e.body as { body: string }).body;
    const taskId = (e.meta as { taskId: string }).taskId;
    drafts.append(commentDraftKey(taskId), body);
    toast(t('comment.failed', { message: messageOf(err) }), { kind: 'error', ms: 8000 });
  },
});

outbox.register('comment.update', {
  done: (e, r) => upsertComment(e.projectId, r as CommentDto),
  fail: (e, err) => {
    const current = err instanceof ApiError && err.code === 'version_conflict' ? (err.body.current as CommentDto) : null;
    const body = (e.body as { body: string }).body;
    if (current) {
      upsertComment(e.projectId, current);
      toast(t('comment.conflict'), { kind: 'error', ms: 12_000, action: { label: t('sync.keepMine'), run: () => editComment(current, body) } });
      return;
    }
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

outbox.register('comment.remove', {
  done: (e) => {
    const m = e.meta as { taskId: string; commentId: string };
    removeComment(e.projectId, m.taskId, m.commentId);
  },
  fail: (_e, err) => {
    if (err instanceof ApiError && err.status === 404) return;
    toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' });
  },
});

outbox.register('comment.restore', { done: (e, r) => upsertComment(e.projectId, r as CommentDto), fail: (_e, err) => void toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' }) });
outbox.register('comment.react', { done: (e, r) => upsertComment(e.projectId, r as CommentDto), fail: (_e, err) => void toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' }) });
outbox.register('task.follow', {
  done: (e, r) => queryClient.setQueryData(keys.follow(e.projectId, (e.meta as { taskId: string }).taskId), r as FollowDto),
  fail: (_e, err) => void toast(t('sync.failed', { message: messageOf(err) }), { kind: 'error' }),
});
outbox.register('notification.read', {
  done: () => void queryClient.invalidateQueries({ queryKey: keys.notifications }),
  fail: () => void queryClient.invalidateQueries({ queryKey: keys.notifications }),
});

export function addComment(projectId: string, taskId: string, authorId: string, body: string) {
  const opId = newOpId();
  const now = new Date().toISOString();
  const temp: CommentDto = { id: `tmp-${opId}`, projectId, taskId, authorId, body, reactions: [], createdAt: now, editedAt: null, version: 0 };
  void outbox
    .enqueue({ opId, kind: 'comment.create', projectId, method: 'POST', url: `/api/tasks/${taskId}/comments`, body: { opId, body }, overlay: { type: 'comment', comment: temp }, meta: { taskId } })
    .catch(() => {});
}

export function editComment(c: CommentDto, body: string) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'comment.update',
      projectId: c.projectId,
      method: 'PATCH',
      url: `/api/comments/${c.id}`,
      body: { opId, expectedVersion: c.version, body },
      overlay: { type: 'commentPatch', taskId: c.taskId, commentId: c.id, fields: { body, editedAt: new Date().toISOString() } },
    })
    .catch(() => {});
}

export function deleteComment(c: CommentDto) {
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'comment.remove',
      projectId: c.projectId,
      method: 'DELETE',
      url: `/api/comments/${c.id}`,
      body: { opId },
      overlay: { type: 'commentRemove', taskId: c.taskId, commentId: c.id },
      meta: { taskId: c.taskId, commentId: c.id },
    })
    .catch(() => {});
  toast(t('comment.deleted'), {
    action: {
      label: t('common.undo'),
      run: () => {
        if (outbox.cancel(opId)) return;
        const restoreOp = newOpId();
        void outbox
          .enqueue({ opId: restoreOp, kind: 'comment.restore', projectId: c.projectId, method: 'POST', url: `/api/comments/${c.id}/restore`, body: { opId: restoreOp }, overlay: { type: 'comment', comment: c } })
          .catch(() => {});
      },
    },
  });
}

/** Reaktion umschalten – sofort sichtbar, als Delta gespeichert */
export function toggleReaction(c: CommentDto, emoji: string, userId: string) {
  const has = c.reactions.some((r) => r.emoji === emoji && r.userIds.includes(userId));
  const reactions = has
    ? c.reactions.map((r) => (r.emoji === emoji ? { ...r, userIds: r.userIds.filter((u) => u !== userId) } : r)).filter((r) => r.userIds.length)
    : c.reactions.some((r) => r.emoji === emoji)
      ? c.reactions.map((r) => (r.emoji === emoji ? { ...r, userIds: [...r.userIds, userId] } : r))
      : [...c.reactions, { emoji, userIds: [userId] }];
  const opId = newOpId();
  void outbox
    .enqueue({
      opId,
      kind: 'comment.react',
      projectId: c.projectId,
      method: 'POST',
      url: `/api/comments/${c.id}/reactions`,
      body: { opId, emoji, on: !has },
      overlay: { type: 'commentPatch', taskId: c.taskId, commentId: c.id, fields: { reactions } },
    })
    .catch(() => {});
}

export function setFollow(projectId: string, taskId: string, on: boolean) {
  const opId = newOpId();
  queryClient.setQueryData<FollowDto>(keys.follow(projectId, taskId), (old) => (old ? { following: on, count: old.count + (on === old.following ? 0 : on ? 1 : -1) } : old));
  void outbox.enqueue({ opId, kind: 'task.follow', projectId, method: 'PUT', url: `/api/tasks/${taskId}/follow`, body: { opId, on }, meta: { taskId } }).catch(() => {});
}

export function markRead(ids: string[] | 'all') {
  const opId = newOpId();
  void outbox
    .enqueue({ opId, kind: 'notification.read', projectId: '', method: 'POST', url: '/api/notifications/read', body: ids === 'all' ? { opId, all: true } : { opId, ids } })
    .catch(() => {});
}
